import { useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowLeft, Sparkles, Rocket, Bug, Star, RefreshCw 
} from "lucide-react";

export default function AtualizacoesPage() {
  useEffect(() => {
    const root = document.documentElement;
    const hadDark = root.classList.contains("dark");
    if (hadDark) {
      root.classList.remove("dark");
      root.classList.add("light");
    }
    return () => {
      if (hadDark) {
        root.classList.remove("light");
        root.classList.add("dark");
      }
    };
  }, []);

  const updates = [
    {
      version: "1.2.0",
      date: "Maio 2024",
      type: "new",
      title: "Arquitetura Glass Blue Pro",
      desc: "Lançamento da nova interface visual com foco em experiência do usuário e alta performance.",
      items: ["Novo Dashboard Admin", "Tema dinâmico Light/Dark", "Pop-ups interativos no calendário"]
    },
    {
      version: "1.1.5",
      date: "Abril 2024",
      type: "fix",
      title: "Otimização de Web Workers",
      desc: "Melhoria de 40% na velocidade de processamento de XMLs em lote.",
      items: ["Correção de vazamento de memória", "Suporte a arquivos de até 50MB", "Validação SHA-256 aprimorada"]
    },
    {
      version: "1.0.0",
      date: "Março 2024",
      type: "rocket",
      title: "Lançamento Oficial",
      desc: "A plataforma entra em operação com suporte completo a NF-e e NFC-e.",
      items: ["Processamento 100% local", "Extração de tributos", "Exportação para Excel/JSON"]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#EAF4FF] via-[#F5FAFF] to-[#E6F0FF] text-slate-900 font-sans selection:bg-blue-200 relative overflow-hidden pb-20">
      
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute top-[-10%] right-[-10%] size-[600px] rounded-full bg-[radial-gradient(circle_at_center,#33CCFF22,transparent_60%)] blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-10%] size-[500px] rounded-full bg-[radial-gradient(circle_at_center,#1A66CC11,transparent_60%)] blur-3xl" />
      </div>

      <div className="max-w-4xl mx-auto px-6 pt-12 relative z-10">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-bold text-blue-600 hover:text-blue-700 mb-8 group transition-all"
        >
          <div className="size-8 rounded-lg bg-white shadow-sm border border-blue-100 flex items-center justify-center group-hover:-translate-x-1 transition-transform">
            <ArrowLeft className="size-4" />
          </div>
          Voltar para o Início
        </Link>

        <div className="relative">
          <div className="relative bg-white/60 backdrop-blur-3xl rounded-[2rem] border border-white/80 p-8 lg:p-12 shadow-[0_30px_80px_-20px_rgba(26,102,204,0.15),inset_0_1px_1px_rgba(255,255,255,1)]">
            
            <header className="flex flex-col sm:flex-row sm:items-center gap-5 mb-16 pb-8 border-b border-blue-100/50">
              <div className="size-14 rounded-2xl bg-gradient-to-br from-[#33CCFF] to-[#1A66CC] grid place-items-center text-white shadow-lg shadow-blue-500/30">
                <Sparkles className="size-7" />
              </div>
              <div>
                <h1 className="text-3xl font-black tracking-tight text-slate-900 mb-1">Atualizações</h1>
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400">
                  Novidades e Melhorias Contínuas
                </p>
              </div>
            </header>

            <div className="space-y-16 relative">
              {/* Linha do tempo vertical */}
              <div className="absolute left-7 top-0 bottom-0 w-px bg-gradient-to-b from-blue-200 via-blue-100 to-transparent hidden sm:block" />

              {updates.map((update, idx) => (
                <div key={idx} className="relative sm:pl-20 group">
                  {/* Marcador na linha do tempo */}
                  <div className="absolute left-5 top-0 size-4 rounded-full bg-white border-4 border-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.5)] z-10 hidden sm:block group-hover:scale-125 transition-transform" />
                  
                  <div className="bg-white/40 border border-white/60 rounded-3xl p-6 lg:p-8 shadow-sm hover:shadow-md transition-all hover:-translate-y-1">
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <span className="px-3 py-1 rounded-full bg-blue-600 text-white text-[10px] font-black uppercase tracking-widest">
                        v{update.version}
                      </span>
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                        {update.date}
                      </span>
                      {update.type === "new" && <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-600 uppercase"><Star className="size-3" /> Novidade</span>}
                      {update.type === "fix" && <span className="flex items-center gap-1 text-[10px] font-bold text-amber-600 uppercase"><Bug className="size-3" /> Correção</span>}
                      {update.type === "rocket" && <span className="flex items-center gap-1 text-[10px] font-bold text-purple-600 uppercase"><Rocket className="size-3" /> Lançamento</span>}
                    </div>

                    <h2 className="text-xl font-black text-slate-900 mb-3">{update.title}</h2>
                    <p className="text-sm text-slate-600 leading-relaxed mb-6 italic">{update.desc}</p>
                    
                    <ul className="grid sm:grid-cols-2 gap-3 list-none p-0">
                      {update.items.map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-xs font-bold text-slate-500">
                          <RefreshCw className="size-3 text-blue-400" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
